/**
 * AEO pack (Oct 2026) copy shared by first-HTML SSR, React pages, JSON-LD and
 * the llms files. Text is Harvey-cleared (HARVEY-REVIEW-2026-10-19). Harvey R6
 * (Oct 20, 2026) allows the investor Integrity Fund fee and keeps the annual
 * regional center fee off these strings. Edit only with a new legal review.
 * No em dashes in this file's copy.
 */

/** Ship date of the AEO pack: visible "Updated" lines and schema dateModified. */
export const AEO_SHIP_DATE = "2026-10-20";

const SITE_ORIGIN = "https://verascap.com";

export const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;

export const SITE_FOOTER_DISCLAIMER =
  "Veras Capital is not a law firm and does not give legal, tax or investment advice. Nothing on this site is an offer to sell or a solicitation of an offer to buy any security; any offering is made only by its offering documents to eligible investors. EB-5 investments are at risk, and visa approval, timing and returns are not guaranteed.";

export const ATTORNEY_CONNECT_LINE =
  "We can connect you with an experienced EB-5 attorney. The attorney works for you, not Veras, and Veras receives no referral fee.";

export const HOME_DEFINITION =
  "Veras Capital is an EB-5 platform. We help foreign investors understand the EB-5 visa, compare EB-5 projects with clear diligence questions, and track visa data. We are not a law firm. We can connect you with an experienced EB-5 attorney. The attorney works for you, not Veras, and Veras receives no referral fee.";

/** First two sentences of HOME_DEFINITION, for meta descriptions. */
export const HOME_META_DESCRIPTION =
  "Veras Capital is an EB-5 platform. We help foreign investors understand the EB-5 visa, compare EB-5 projects with clear diligence questions, and track visa data.";

export const HOME_QUESTION = "What does Veras Capital do for EB-5 investors?";

export const HOME_ANSWER =
  "It depends on where you are. If you are new to EB-5, start with our guide and the live Visa Bulletin. If you are comparing projects, we give you the questions to ask about jobs, rural status, fees and risk. If you are ready to file, you need an immigration attorney. Veras does not give legal advice and does not prepare source-of-funds files. We can connect you with an experienced EB-5 attorney. The attorney works for you, not Veras, and Veras receives no referral fee.";

export const HOME_INVESTMENT_AS_OF =
  "EB-5 minimum investment, as of October 2026: $800,000 in a targeted employment area (TEA) or $1,050,000 elsewhere. Source: USCIS.";

export const LLMS_SUMMARY =
  "Veras Capital is an EB-5 platform. We help foreign investors understand the EB-5 visa, compare EB-5 projects with clear diligence questions, and track visa data. We are not a law firm. We can connect you with an experienced EB-5 attorney, who works for you.";

export const ABOUT_DEFINITION =
  "Veras Capital is an EB-5 platform. We help foreign nationals understand the EB-5 investor visa and compare EB-5 projects with plain-English diligence. We are not a law firm, an immigration attorney or a registered broker-dealer, and we do not give investment advice.";

export const ABOUT_META_DESCRIPTION =
  "Veras Capital is an EB-5 platform. We help foreign nationals understand the EB-5 investor visa and compare EB-5 projects with plain-English diligence.";

export const ABOUT_QUESTION = "Who is Veras Capital, and what do you do?";

export const ABOUT_ANSWER =
  "It depends on what you need. We help you learn how EB-5 works, read the Visa Bulletin, and ask the right questions about a project. We do not help with source of funds. Documenting the lawful source and path of your money is your attorney's work. We can connect you with an experienced EB-5 attorney. The attorney works for you, not Veras, and Veras receives no referral fee. For legal, tax or immigration advice on your own case, speak with your attorney or tax advisor.";

export const ABOUT_DOES_NOT_DO_HEADING = "What Veras does not do";

export const ABOUT_DOES_NOT_DO = [
  "Legal or immigration advice",
  "Source-of-funds documentation",
  "Promises about visa approval, timing or investment returns",
  "Tax advice",
] as const;

export const INVESTMENT_AS_OF_LINE =
  "As of October 2026, the EB-5 minimum investment is $800,000 in a targeted employment area (TEA) or $1,050,000 elsewhere. Source: USCIS, About the EB-5 Visa Classification.";

// REMINDER (Nov 30, 2026): replace I526E_FEE_AS_OF_LINE and
// INTEGRITY_FUND_FEE_LINE with I526E_FEE_LINE_AFTER_NOV_30_2026 on or after
// November 30, 2026 (Harvey C6, updated by Harvey R6). The fee lines are
// static copy on purpose; they are not driven by a feed.
export const I526E_FEE_AS_OF_LINE =
  "As of October 2026, the USCIS filing fee for Form I-526E is $3,675. It rises to $7,850 for filings postmarked on or after November 30, 2026. Source: USCIS fee rule, 91 FR 61940; see uscis.gov/fees.";

/** Investor per-petition fee. Not the annual regional center fee. Harvey R6. */
export const INTEGRITY_FUND_FEE_LINE =
  "Regional center investors also pay a separate EB-5 Integrity Fund fee with Form I-526E: $1,000 today, $1,100 if postmarked on or after November 30, 2026. Source: 91 FR 61940; 8 CFR 106.2(d)(1); see uscis.gov/fees.";

export const I526E_FEE_LINE_AFTER_NOV_30_2026 =
  "As of November 30, 2026, the I-526E filing fee is $7,850, plus a $1,100 EB-5 Integrity Fund fee.";

export const I526E_FEE_SWAP_DATE = "2026-11-30";

interface BulletinRow {
  country: string;
  unreserved: string;
  rural: string;
  highUnemployment: string;
  infrastructure: string;
}

interface BulletinLike {
  month: string;
  year: number;
  finalActionDates: BulletinRow[];
}

function isCurrent(value: string): boolean {
  const v = value.trim().toLowerCase();
  return v === "c" || v === "current";
}

function countryLabel(country: string): string {
  return country.replace(/\s*\(.*\)\s*$/, "").trim();
}

const RESERVED_CATEGORIES = [
  { key: "rural", name: "Rural" },
  { key: "highUnemployment", name: "High unemployment" },
  { key: "infrastructure", name: "Infrastructure" },
] as const;

/**
 * Each reserved pool is its own sentence. A cut-off in one category names
 * that category and its date, and does not pull the other two pools with it.
 */
function reservedAsOf(rows: BulletinRow[]): string {
  const byCategory = RESERVED_CATEGORIES.map((cat) => ({
    ...cat,
    cutOff: rows.filter((r) => !isCurrent(r[cat.key] ?? "")),
  }));
  if (byCategory.every((cat) => cat.cutOff.length === 0)) {
    return "reserved categories (rural, high unemployment, infrastructure) are Current for all countries.";
  }
  return byCategory
    .map((cat) => {
      if (cat.cutOff.length === 0) {
        return `${cat.name} is Current for all countries.`;
      }
      const who = cat.cutOff
        .map((r) => `${countryLabel(r.country)} (${r[cat.key]})`)
        .join(" and ");
      return `${cat.name} is Current for all countries except ${who}.`;
    })
    .join(" ");
}

/**
 * Visa Bulletin as-of line, generated from the same data /api/visa-bulletin
 * serves so it follows the monthly ingest (never hand-edit the month).
 */
export function buildVisaBulletinAsOfLine(bulletin: BulletinLike): string {
  const rows = bulletin.finalActionDates ?? [];
  const head = `As of the ${bulletin.month} ${bulletin.year} Visa Bulletin (Final Action Dates):`;

  const reserved = reservedAsOf(rows);

  const unreservedCutOff = rows.filter((r) => !isCurrent(r.unreserved ?? ""));
  const unreserved =
    unreservedCutOff.length === 0
      ? "Unreserved is Current for all countries."
      : `Unreserved is Current for all countries except ${unreservedCutOff
          .map((r) => `${countryLabel(r.country)} (${r.unreserved})`)
          .join(" and ")}.`;

  const hold =
    bulletin.month === "November" &&
    bulletin.year === 2026 &&
    rows.length > 0 &&
    rows.every(
      (r) =>
        isCurrent(r.rural) &&
        isCurrent(r.highUnemployment) &&
        isCurrent(r.infrastructure),
    ) &&
    rows.some(
      (r) => countryLabel(r.country) === "China" && r.unreserved === "01 Dec 2016",
    ) &&
    rows.some(
      (r) => countryLabel(r.country) === "India" && r.unreserved === "01 Dec 2023",
    )
      ? " No change from October."
      : "";

  return `${head} ${reserved} ${unreserved}${hold}`;
}

/** "Published January 1, 2026 · Updated October 20, 2026" from ISO dates. */
export function formatPublishedUpdated(published: string, updated: string): string {
  return `Published ${formatIsoDateLong(published)} · Updated ${formatIsoDateLong(updated)}`;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function formatIsoDateLong(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

export const ORGANIZATION_DESCRIPTION =
  "EB-5 platform. Helps foreign investors understand the EB-5 visa and compare EB-5 projects. Not a law firm, an immigration attorney or a registered broker-dealer, and does not give investment advice. Does not prepare source-of-funds documentation. Can connect investors with an experienced EB-5 attorney. The attorney works for you, not Veras.";

export function buildAboutJsonLd(): object[] {
  return [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      url: `${SITE_ORIGIN}/about`,
      name: "About Veras Capital",
      dateModified: AEO_SHIP_DATE,
      mainEntity: {
        "@type": "Organization",
        "@id": ORGANIZATION_ID,
        name: "Veras Capital, LLC",
        url: `${SITE_ORIGIN}/`,
        email: "hello@verascap.com",
        description: ORGANIZATION_DESCRIPTION,
        knowsAbout: [
          "EB-5 Immigrant Investor Program",
          "EB-5 Visa Bulletin",
          "Form I-526E",
          "Form I-956F",
          "Targeted employment areas",
        ],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: ABOUT_QUESTION,
          acceptedAnswer: { "@type": "Answer", text: ABOUT_ANSWER },
        },
      ],
    },
  ];
}

export function buildHomeOrganizationJsonLd(): object {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: "Veras Capital",
    legalName: "Veras Capital, LLC",
    url: `${SITE_ORIGIN}/`,
    logo: `${SITE_ORIGIN}/logo-dark.svg`,
    email: "hello@verascap.com",
    description: ORGANIZATION_DESCRIPTION,
  };
}

// /source-of-funds (cluster 4 of the six-cluster pack).
export const SOF_PUBLISHED = "2026-02-10";

export const SOF_DEFINITION =
  "EB-5 source of funds is the documentation that shows your investment money was lawfully earned and how it moved to the investment.";

export const SOF_QUESTION = "How do I prove my EB-5 source of funds?";

export const SOF_ATTORNEY_LINK_TEXT = "Talk to an EB-5 attorney";

export const SOF_ATTORNEY_HREF = "/schedule-call";

export const SOF_ATTORNEY_LINE =
  "Veras does not prepare source-of-funds files. Your attorney does. We can connect you with an experienced EB-5 attorney. The attorney works for you, not Veras.";

export const SOF_ANSWER =
  "It depends on where your money came from. Salary, a business, a property sale, a gift or a loan can each work if every step is documented. USCIS publishes its standard in the Policy Manual, Volume 6, Part G, Chapter 2. Your immigration attorney builds the file from that standard and your facts. Veras does not prepare source-of-funds files. We can connect you with an experienced EB-5 attorney. The attorney works for you, not Veras, and Veras receives no referral fee. Start early.";

// /visa-bulletin tool (cluster 1 shares copy with the explainer page).
export const VISA_BULLETIN_DEFINITION =
  "The EB-5 Visa Bulletin is a monthly State Department chart. It shows which EB-5 applicants can take the next step, based on country of birth, category and priority date.";

export const VISA_BULLETIN_QUESTION = "Is my EB-5 category current this month?";

export const VISA_BULLETIN_ANSWER =
  "It depends on your country of birth and your category. As of the November 2026 bulletin, the reserved categories (rural, high unemployment, infrastructure) are Current for every country. Unreserved is Current for most countries. China and India have cut-off dates in unreserved. \"Current\" means a visa number is available now. It does not mean USCIS has finished processing your petition. That is a separate clock. Ask your attorney how your priority date fits.";

export const SCHEDULE_CALL_H1 = "Schedule a call with Veras Capital";
